import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  AspectRatio,
  IconButton,
} from '@mui/joy';
import {
  CloudArrowUp as CloudUpload,
  Trash as Delete,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import { useDropzone } from 'react-dropzone';
import api from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/apiError';
import { useRegisterStepSave, type RegisterStepSave } from '../useRegisterStepSave';

interface UploadedFile {
  id?: number;
  file: File;
  preview: string;
  uploading?: boolean;
  error?: string;
}

interface MarketingPhotosStepProps {
  registerSave: RegisterStepSave;
}

export const MarketingPhotosStep: React.FC<MarketingPhotosStepProps> = ({
  registerSave,
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Use FileReader to create data URLs instead of blob URLs
    // Blob URLs are blocked by CSP, but data: URLs are allowed
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setFiles((prev) => [
          ...prev,
          {
            file,
            preview: reader.result as string,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    // Photos are optional — an empty step is a valid one to move past.
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        if (fileData.id) continue; // Already uploaded

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i ? { ...f, uploading: true } : f
          )
        );

        const formData = new FormData();
        formData.append('file', fileData.file);
        formData.append('name', fileData.file.name);
        formData.append('description', 'Uploaded during setup');

        const response = await api.post<{ id: number }>('/api/marketing/media/', formData);

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, id: response.data.id, uploading: false }
              : f
          )
        );
      }

    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to upload photos. Please try again.'));
      // Re-raise so the wizard keeps the user on this step with their files.
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  // Run by the wizard's "Save & Continue" — the only trigger this step has.
  useRegisterStepSave(registerSave, handleUpload);

  return (
    <Box>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Drop zone */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.500' : 'neutral.300',
          borderRadius: 'md',
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'primary.50' : 'neutral.50',
          transition: 'all 0.2s ease',
          mb: 3,
          '&:hover': {
            borderColor: 'primary.400',
            backgroundColor: 'primary.50',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload size={48} color="var(--joy-palette-neutral-500)" style={{ marginBottom: 8 }} />
        <Typography level="title-md" sx={{ mb: 0.5 }}>
          {isDragActive
            ? 'Drop photos here...'
            : 'Drag & drop photos here'}
        </Typography>
        <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
          or click to select files (max 10MB each)
        </Typography>
      </Box>

      {/* Preview grid */}
      {files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 1.5 }}>
            Selected Photos ({files.length})
          </Typography>
          <Grid container spacing={2}>
            {files.map((file, index) => (
              <Grid key={index} xs={6} sm={4} md={3}>
                <Card
                  variant="outlined"
                  sx={{ position: 'relative', overflow: 'hidden' }}
                >
                  <AspectRatio ratio="1">
                    <img
                      src={file.preview}
                      alt={file.file.name}
                      style={{ objectFit: 'cover' }}
                    />
                    {file.uploading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.8)',
                        }}
                      >
                        <CircularProgress size="sm" />
                      </Box>
                    )}
                  </AspectRatio>
                  <IconButton
                    variant="soft"
                    color="danger"
                    size="sm"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                    }}
                    onClick={() => handleRemoveFile(index)}
                    disabled={file.uploading}
                  >
                    <Delete />
                  </IconButton>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Empty state */}
      {files.length === 0 && (
        <Card
          variant="soft"
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'neutral.100',
          }}
        >
          <ImageIcon size={32} color="var(--joy-palette-neutral-500)" style={{ marginBottom: 8 }} />
          <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
            No photos selected yet. You can add them now or skip this step and
            upload photos later from the Marketing section.
          </Typography>
        </Card>
      )}

    </Box>
  );
};

export default MarketingPhotosStep;
